import React from 'react';

import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { UserRound } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { GenerateInsightIcon } from 'src/components/icons/GenerateInsightIcon';
import { buildAiReasoningTooltipContent } from 'src/components/reminders/AiReasoningTooltip';
import type { ReminderListRow } from 'src/components/reminders/ColumnDef';
import { buildColumnMap } from 'src/components/reminders/useReportColumnMap';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  icon: {
    flexShrink: 0,
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    width: '100%',
  },
}));

export function ReminderSourceCell({
  row,
}: GridCellParams<ReminderListRow, ReminderListRow['aiReminderAutomation']>) {
  const { classes } = useStyles();

  if (!row.isAiGenerated) {
    return (
      <div className={classes.row}>
        <UserRound className={classes.icon} size={16} />
        <Typography>Manual</Typography>
      </div>
    );
  }

  const tooltipContent = buildAiReasoningTooltipContent({
    ...row,
    columnMap: buildColumnMap(row.aiReminderAutomation?.report?.columnOptions ?? []),
    documentId: row.document.id,
  });

  return (
    <div
      className={classes.row}
      /**
       * Stop propagation of click and mouse down so the DataGrid doesn't try and
       * focus the cell and force the user to double-click the citation link to open.
       */
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <AlineTooltip
        arrow
        disableInteractive={false}
        placement="top"
        slotProps={{ tooltip: { sx: { maxWidth: 420, px: 2, py: 1 } } }}
        title={tooltipContent}
      >
        <div className={classes.row}>
          <span style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
            <GenerateInsightIcon className={classes.icon} />
          </span>
          <Typography noWrap>AI Automation</Typography>
        </div>
      </AlineTooltip>
    </div>
  );
}
